import { formatDate } from "@angular/common";

const DATE_FORMAT = "yyyy-MM-dd HH:mm:00";
/** Mirrors DateTimeUtil.DATETIME_FORMAT in the backend's shared library. */
const DTO_DATETIME_FORMAT = "dd.MM.yyyy HH:mm:ss";
const DATE_LOCALE = "en";

/** 
 * @param {Object} date: {year: 2023, month: 4, day: 4}
 * @return {string}: Output: 2023-04-04
*/
export const prepareDateForDb = (date: any): string => {
    if (!date) return '';

    let fromDateYear  = date.year.toString();
    let fromDateMonth = date.month.toString();
    let fromDateDay   = date.day.toString();

    if (fromDateMonth.length === 1) fromDateMonth = '0' + fromDateMonth;
    if (fromDateDay.length === 1)   fromDateDay = '0' + fromDateDay;

    return fromDateYear + '-' + fromDateMonth + '-' + fromDateDay;
}

/** 
 * @param {Object} date: {year: 2023, month: 4, day: 4}
 * @return {string}: 4-4-2023
*/
export const convertObjDateToString = (date: any): string => {
    if (!date) return "";
    return date.day + '-' + date.month + '-' + date.year;
}

/**
 * @param {Date} date: new Date()
 * @returns {Object}: {year: 2023, month: 4, day: 4}
*/
export const prepareDateForForm = (date: Date): { year: number, month: number, day: number } => {
   return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}

/**
 * Today as an ngb datepicker value, read off the local calendar.
 *
 * prepareDateForForm would be the obvious thing to call, but it reads the UTC date: here in
 * UTC+2/+3 that is still yesterday until 02:00 or 03:00, so an early-shift operator would open
 * the screen on the wrong day. A date the user reads and searches on has to be the date on the
 * wall behind them.
 *
 * @returns {Object}: {year: 2023, month: 4, day: 4}
*/
export const todayForDatepicker = (): { year: number, month: number, day: number } => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

/** 
 * @param {Date} date: new Date()
 * @return {string}: 04/04/2023 11:13 
*/
export const convertDateToString = (date: Date): string => {
    return dateFormatter.format(date).replace(',', '')
}

/** 
 * @param {any} date: 2023-01-20
 * @returns {Object}: {year: 2023, month: 1, day: 20}
*/
export const prepareDateForUI = (date: any): object | null => {
    if (!date) return null;

    let splitted = date.toString().split("-");
    const dateYear  = +splitted[0];
    const dateMonth = +splitted[1];
    const dateDay   = +splitted[2];
   
    return { year: dateYear, month: dateMonth, day: dateDay };
}

/** 
 * @param {number[]} dateArr: [2023, 1, 26, 16, 6, 39, 420128000]
 * @return {string}: 26.01.2023 - 16:06
*/
export const formatDateFromArray = (dateArr: any): string => {
    if (!dateArr) return '';
    const[year, month, day, hour, minutes] = dateArr;
    return `${addLeadingZero(day)}.${addLeadingZero(month)}.${addLeadingZero(year)} - ${addLeadingZero(hour)}:${addLeadingZero(minutes)}`;
}

/** 
 * @param {Date | Number | String} date: new Date()
 * @return {string}: 2023-04-10 11:35:00
*/
export const formatFullDate = (date: string | number | Date): string => {
    return formatDate(date, DATE_FORMAT, DATE_LOCALE);
}

/**
 * A date and time for a DTO field annotated @JsonFormat(DateTimeUtil.DATETIME_FORMAT).
 *
 * That constant is "dd.MM.yyyy HH:mm:ss", not the "yyyy-MM-dd HH:mm:00" formatFullDate writes.
 * Jackson parses a pattern-annotated field by that pattern alone, so sending the other shape is
 * refused outright with a 400 before any controller code runs. The fields that take
 * formatFullDate carry no annotation and are parsed leniently, which is why both exist.
 *
 * @param {Date | Number | String} date: new Date()
 * @return {string}: 10.04.2023 11:35:00
*/
export const formatDateTimeForDto = (date: string | number | Date): string => {
    return formatDate(date, DTO_DATETIME_FORMAT, DATE_LOCALE);
}

/**
 * Reads back a date a DTO serialised as "dd.MM.yyyy HH:mm:ss".
 *
 * new Date() cannot parse that shape - it is neither ISO nor the American order the engine
 * falls back on - and answers Invalid Date, which a picker then shows as an empty box. The
 * parts are pulled out and handed to the constructor in its own order instead. The seconds are
 * optional, since DATETIME_FORMAT_SHORT omits them.
 *
 * @param {string} value: "29.10.2026 14:22:00"
 * @returns {Date | null}: null when the string is absent or not of that shape
*/
export const parseDtoDateTime = (value: string | null | undefined): Date | null => {
    if (!value) return null;

    const match = /^(\d{2})\.(\d{2})\.(\d{4})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
    if (!match) {
        // An ISO string still parses natively, so an endpoint that is not pattern-annotated
        // goes on working through here as well.
        const fallback = new Date(value);
        return isNaN(fallback.getTime()) ? null : fallback;
    }

    const [, day, month, year, hours, minutes, seconds] = match;
    return new Date(+year, +month - 1, +day, +hours, +minutes, seconds ? +seconds : 0);
}

export const dateFormatter = new Intl.DateTimeFormat("en-GB", {
    year:   "numeric",
    month:  "2-digit",
    day:    "2-digit",
    hour:   "2-digit",
    minute: "2-digit",
});

export const addLeadingZero = (obj: any): string => {
    obj = new String(obj);
    if (obj.length === 1) {
      return '0' + obj;
    }
    return obj;
}